import React, { useState, useEffect, useCallback } from 'react';
import api, {
  getChartSnapshotConfigs,
  createChartSnapshotConfig,
  getChartSnapshotConfigDetails,
  updateChartSnapshotConfig,
  deleteChartSnapshotConfig
} from '../api/api'; // Adjust path as needed

const INDICATOR_SOURCE_CHOICES = ["open", "high", "low", "close", "hl2", "hlc3", "ohlc4"];

// Color Conversion Utilities
function hexToRgbString(hex) {
  if (typeof hex !== 'string' || !hex) return 'rgb(0,0,0)';
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    console.warn(`Invalid hex color for hexToRgbString: ${hex}, returning rgb(0,0,0)`);
    return 'rgb(0,0,0)';
  }
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgb(${r},${g},${b})`;
}

function rgbStringToHex(rgbString) {
  if (typeof rgbString !== 'string' || !rgbString) return '#000000';
  const namedColorToHexMap = {
    "blue": "#0000FF", "red": "#FF0000", "green": "#008000", "darkgreen": "#006400",
    "gray": "#808080", "purple": "#800080", "teal": "#008080",
    "lightgray": "#D3D3D3", "orange": "#FFA500", "yellow": "#FFFF00",
  };
  if (namedColorToHexMap[rgbString.toLowerCase()]) {
    return namedColorToHexMap[rgbString.toLowerCase()];
  }
  const match = rgbString.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/);
  if (!match) {
    console.warn(`Invalid RGB/A string for rgbStringToHex: ${rgbString}, returning #000000`);
    return '#000000';
  }
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}

const DEFAULT_INDICATOR_SETTINGS = {
  emas: {
    enabled: true, periods: [21, 50, 100], source: "close",
    overrides: [ 
        {"Plot.color": "rgb(0,0,255)", "Plot.linewidth": 1},
        {"Plot.color": "rgb(255,0,0)", "Plot.linewidth": 1},
        {"Plot.color": "rgb(0,128,0)", "Plot.linewidth": 1}
    ]
  },
  dmi: {
    enabled: true, di_length: 14, adx_smoothing: 14,
    overrides: {
        "+DI.color": "rgb(0,128,0)", "+DI.linewidth": 1, "+DI.visible": true,
        "-DI.color": "rgb(255,0,0)", "-DI.linewidth": 1, "-DI.visible": true,
        "ADX.color": "rgb(0,0,255)", "ADX.linewidth": 1, "ADX.visible": true
    }
  },
  stoch_rsi: {
    enabled: true, rsi_length: 14, "stoch_length": 14, k_smooth: 3, d_smooth: 3,
    overrides: {
        "%K.color": "rgb(0,0,255)", "%K.linewidth": 1, "%K.visible": true,
        "%D.color": "rgb(255,0,0)", "%D.linewidth": 1, "%D.visible": true,
        "UpperLimit.color": "rgb(128,128,128)", "UpperLimit.value": 80, "UpperLimit.visible": true,
        "LowerLimit.color": "rgb(128,128,128)", "LowerLimit.value": 20, "LowerLimit.visible": true
    }
  },
  rsi: {
    enabled: false, length: 14, smoothingLine: "SMA", "smoothingLength": 14,
    "overrides": {
        "Plot.color": "rgb(128,0,128)", "Plot.linewidth": 1, "Plot.visible": true,
        "UpperLimit.color": "rgb(128,128,128)", "UpperLimit.value": 70, "UpperLimit.visible": true,
        "LowerLimit.color": "rgb(128,128,128)", "LowerLimit.value": 30, "LowerLimit.visible": true,
        "MiddleLimit.color": "rgb(211,211,211)", "MiddleLimit.value": 50, "MiddleLimit.visible": false 
    }
  },
  macd: {
    enabled: false, "fast_length": 12, "slow_length": 26, "signal_length": 9, source: "close",
    overrides: {
        "MACD.color": "rgb(0,0,255)", "MACD.linewidth": 1, "MACD.visible": true,
        "Signal.color": "rgb(255,0,0)", "Signal.linewidth": 1, "Signal.visible": true,
        "Histogram.visible": true
    }
  },
  cmf: {
    enabled: false, length: 20,
    overrides: {
        "Plot.color": "rgb(0,128,128)", "Plot.linewidth": 1, "Plot.visible": true,
        "Zero.color": "rgb(128,128,128)", "Zero.linewidth": 1, "Zero.visible": true
    }
  }
};

const mergeWithDefaultSettings = (settingsToMerge, defaultSettings) => {
  const merged = JSON.parse(JSON.stringify(defaultSettings)); 
  for (const indicatorKey in settingsToMerge) {
    if (!merged[indicatorKey]) merged[indicatorKey] = JSON.parse(JSON.stringify(defaultSettings[indicatorKey] || { enabled: false, overrides: indicatorKey === 'emas' ? [] : {} })); // Ensure key exists
    
    if (defaultSettings[indicatorKey]) { 
        for (const paramKey in settingsToMerge[indicatorKey]) {
            if (paramKey !== 'overrides' && merged[indicatorKey].hasOwnProperty(paramKey)) {
                merged[indicatorKey][paramKey] = settingsToMerge[indicatorKey][paramKey];
            }
        }
        if (settingsToMerge[indicatorKey].hasOwnProperty('overrides')) {
            if (indicatorKey === 'emas') { 
                merged[indicatorKey].overrides = JSON.parse(JSON.stringify(settingsToMerge[indicatorKey].overrides));
                const defaultEmaOverride = defaultSettings.emas.overrides[0] || {"Plot.color": "rgb(128,128,128)", "Plot.linewidth": 1};
                const periodsLength = merged[indicatorKey].periods?.length || 0;
                while (merged[indicatorKey].overrides.length < periodsLength ) {
                    merged[indicatorKey].overrides.push(JSON.parse(JSON.stringify(defaultEmaOverride)));
                }
                if (periodsLength > 0) {
                   merged[indicatorKey].overrides.length = periodsLength; 
                } else {
                   merged[indicatorKey].overrides = []; 
                }
            } else { 
                merged[indicatorKey].overrides = { 
                    ...(defaultSettings[indicatorKey].overrides || {}), 
                    ...(settingsToMerge[indicatorKey].overrides || {}) 
                };
            }
        } else { 
            merged[indicatorKey].overrides = JSON.parse(JSON.stringify(defaultSettings[indicatorKey].overrides || (indicatorKey === 'emas' ? [] : {})));
        }
    } else { 
      merged[indicatorKey] = JSON.parse(JSON.stringify(settingsToMerge[indicatorKey]));
    }
  }
   for (const defaultIndicatorKey in defaultSettings) { 
    if (!merged.hasOwnProperty(defaultIndicatorKey)) {
      merged[defaultIndicatorKey] = JSON.parse(JSON.stringify(defaultSettings[defaultIndicatorKey]));
    } else { 
        if (defaultIndicatorKey !== 'emas' && defaultSettings[defaultIndicatorKey].overrides && merged[defaultIndicatorKey].overrides) {
            for(const overrideKey in defaultSettings[defaultIndicatorKey].overrides) {
                if (!merged[defaultIndicatorKey].overrides.hasOwnProperty(overrideKey)) {
                    merged[defaultIndicatorKey].overrides[overrideKey] = defaultSettings[defaultIndicatorKey].overrides[overrideKey];
                }
            }
        }
    }
  }
  return merged;
};

const ChartSnapshotConfigManager = () => {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configName, setConfigName] = useState('');
  const [isGlobalConfig, setIsGlobalConfig] = useState(false); // New state for global flag
  const [indicatorSettings, setIndicatorSettings] = useState(JSON.parse(JSON.stringify(DEFAULT_INDICATOR_SETTINGS)));
  const [currentUserDetails, setCurrentUserDetails] = useState(null);
  const [userLoadingError, setUserLoadingError] = useState(null);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
  const data = await api.get('/accounts/me/'); // api interceptor returns data directly
  setCurrentUserDetails(data);
      } catch (err) {
        console.error("Failed to fetch current user details:", err);
        setUserLoadingError(err.message || "Could not load user data.");
        // Handle error appropriately, e.g., show a message or restrict functionality
      }
    };
    fetchCurrentUser();
  }, []);
  
  const currentUserId = currentUserDetails ? String(currentUserDetails.id) : null;
  const currentUserIsStaff = currentUserDetails ? currentUserDetails.is_staff : false;

  const fetchConfigs = useCallback(async () => {
    setIsLoading(true); setError(null);
    try { const data = await getChartSnapshotConfigs(); setConfigs(data || []); } 
    catch (err) { setError(err.message || 'Failed to fetch configurations.'); setConfigs([]); } 
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { 
    fetchConfigs(); 
    // If currentUserId is part of dependency for fetchConfigs or other logic, add it.
    // For now, fetchConfigs doesn't depend on currentUserId directly for fetching all configs.
  }, [fetchConfigs]);

  const resetForm = () => { 
    setConfigName(''); 
    setIsGlobalConfig(false); // Reset isGlobalConfig
    setIndicatorSettings(JSON.parse(JSON.stringify(DEFAULT_INDICATOR_SETTINGS))); 
    setEditingConfig(null); 
    setError(null); 
  };
  const handleCreateNew = () => { resetForm(); setShowForm(true); };

  const handleEdit = async (configToEdit) => {
    resetForm(); setIsLoading(true);
    try {
      const detailedConfig = await getChartSnapshotConfigDetails(configToEdit.id);
      setConfigName(detailedConfig.name);
      setIsGlobalConfig(detailedConfig.is_global || false); // Set isGlobalConfig from fetched data
      setIndicatorSettings(mergeWithDefaultSettings(detailedConfig.indicator_settings, DEFAULT_INDICATOR_SETTINGS));
      setEditingConfig(detailedConfig); setShowForm(true);
    } catch (err) { setError(err.message || `Failed to load config ${configToEdit.name} for editing.`); } 
    finally { setIsLoading(false); }
  };

  const handleDelete = async (configId) => {
    // In a real app, you might want to double-check ownership/permissions here too before confirming
    if (window.confirm('Are you sure you want to delete this configuration template?')) {
      setIsLoading(true); setError(null);
      try { await deleteChartSnapshotConfig(configId); fetchConfigs(); } 
      catch (err) { setError(err.message || 'Failed to delete configuration template.'); } 
      finally { setIsLoading(false); }
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault(); setIsLoading(true); setError(null);
    const payload = { 
      name: configName, 
      is_global: isGlobalConfig, // Include is_global in payload
      indicator_settings: indicatorSettings 
    };
    try {
      if (editingConfig) await updateChartSnapshotConfig(editingConfig.id, payload);
      else await createChartSnapshotConfig(payload);
      setShowForm(false); fetchConfigs(); resetForm();
    } catch (err) {
      const errorData = err.response?.data;
      if (typeof errorData === 'object' && errorData !== null) {
        let messages = [];
        for (const key in errorData) messages.push(`${key}: ${Array.isArray(errorData[key]) ? errorData[key].join(', ') : errorData[key]}`);
        setError(messages.join('; ') || 'Failed to save configuration.');
      } else setError(err.message || 'Failed to save configuration.');
    } finally { setIsLoading(false); }
  };
  
  const handleIndicatorToggle = (indicatorKey) => setIndicatorSettings(prev => ({ ...prev, [indicatorKey]: { ...prev[indicatorKey], enabled: !prev[indicatorKey].enabled } }));
  const handleIndicatorParamChange = (indicatorKey, paramKey, value, isNumber = false) => setIndicatorSettings(prev => ({ ...prev, [indicatorKey]: { ...prev[indicatorKey], [paramKey]: isNumber ? (value === '' ? null : Number(value)) : value } }));

  const handleEmaPeriodsChange = (index, value) => {
    const newPeriods = [...indicatorSettings.emas.periods]; newPeriods[index] = value === '' ? null : Number(value);
    const newOverrides = [...indicatorSettings.emas.overrides];
    const defaultEmaOverride = DEFAULT_INDICATOR_SETTINGS.emas.overrides[0] || {"Plot.color": "rgb(128,128,128)", "Plot.linewidth": 1};
    while (newOverrides.length < newPeriods.length) newOverrides.push(JSON.parse(JSON.stringify(defaultEmaOverride)));
    if (newOverrides.length > newPeriods.length) newOverrides.length = newPeriods.length;
    setIndicatorSettings(prev => ({ ...prev, emas: { ...prev.emas, periods: newPeriods, overrides: newOverrides } }));
  };
  const addEmaPeriod = () => {
    const newPeriods = [...indicatorSettings.emas.periods, 0]; 
    const newOverrides = [...indicatorSettings.emas.overrides];
    const defaultEmaOverride = DEFAULT_INDICATOR_SETTINGS.emas.overrides[0] || {"Plot.color": "rgb(128,128,128)", "Plot.linewidth": 1};
    newOverrides.push(JSON.parse(JSON.stringify(defaultEmaOverride)));
    setIndicatorSettings(prev => ({ ...prev, emas: { ...prev.emas, periods: newPeriods, overrides: newOverrides } }));
  };
  const removeEmaPeriod = (index) => {
    const newPeriods = indicatorSettings.emas.periods.filter((_, i) => i !== index);
    const newOverrides = indicatorSettings.emas.overrides.filter((_, i) => i !== index);
    setIndicatorSettings(prev => ({ ...prev, emas: { ...prev.emas, periods: newPeriods, overrides: newOverrides } }));
  };

  const handleGenericOverrideChange = (indicatorKey, plotKey, value) => {
    const defaultValue = DEFAULT_INDICATOR_SETTINGS[indicatorKey]?.overrides?.[plotKey];
    let pValue = value; 
    if (typeof defaultValue === 'boolean') pValue = Boolean(value);
    else if (typeof defaultValue === 'number') pValue = value === '' ? null : Number(value);
    setIndicatorSettings(prev => { const n = JSON.parse(JSON.stringify(prev)); if (!n[indicatorKey].overrides) n[indicatorKey].overrides = {}; n[indicatorKey].overrides[plotKey] = pValue; return n; });
  };
  const handleEmaSpecificOverrideChange = (periodIndex, plotKey, value) => {
    const defaultValue = DEFAULT_INDICATOR_SETTINGS.emas.overrides[0]?.[plotKey];
    let pValue = value; 
    if (typeof defaultValue === 'boolean') pValue = Boolean(value);
    else if (typeof defaultValue === 'number') pValue = value === '' ? null : Number(value);
    setIndicatorSettings(prev => { const n = JSON.parse(JSON.stringify(prev)); if (!n.emas.overrides[periodIndex]) n.emas.overrides[periodIndex] = {}; n.emas.overrides[periodIndex][plotKey] = pValue; return n; });
  };
  
  const OverrideInput = ({ id, label, plotKey, value, onChange, typeHint }) => {
    const isColorInput = plotKey.endsWith('.color');
    const inputType = isColorInput ? 'color' : (typeHint === 'boolean' ? 'checkbox' : (typeHint === 'number' ? 'number' : 'text'));
    const isCheckbox = inputType === 'checkbox';
    const inputValue = isColorInput ? rgbStringToHex(value) : (isCheckbox ? undefined : (value ?? '').toString());
    const handleChange = (e) => {
      let processedValue = isCheckbox ? e.target.checked : e.target.value;
      if (isColorInput) processedValue = hexToRgbString(e.target.value);
      onChange(processedValue);
    };
    return (
      <div className="flex items-center space-x-2 mb-1">
        <label htmlFor={id} className="text-xs text-textSecondary capitalize w-28 truncate" title={label}>{label}:</label>
        <input type={inputType} id={id} name={id} checked={isCheckbox ? Boolean(value) : undefined} value={inputValue} onChange={handleChange} className={isCheckbox ? "form-checkbox h-4 w-4 text-primary rounded" : (isColorInput ? "input-color h-6 w-10 p-0 border border-gray-300 rounded" : "input-primary input-xs flex-grow")} min={inputType === 'number' && !plotKey.endsWith('.value') ? 0 : undefined} step={plotKey.endsWith('.linewidth') ? 0.1 : (plotKey.endsWith('.value') && !plotKey.toLowerCase().includes('length') ? 0.01 : undefined)} />
      </div>);
  }; 

  const renderOverridesSection = (indicatorKey) => {
    const currentIndiSettings = indicatorSettings[indicatorKey];
    const defaultIndiOverrides = DEFAULT_INDICATOR_SETTINGS[indicatorKey]?.overrides;
    if (!currentIndiSettings || !defaultIndiOverrides || typeof defaultIndiOverrides !== 'object' || indicatorKey === 'emas') return null;
    return (
      <div className="pl-6 mt-3 space-y-1 border-t border-gray-200 pt-3">
        <h4 className="text-xs font-semibold text-textSecondary mb-1">Visual Overrides:</h4>
        {Object.keys(defaultIndiOverrides).map(plotKey => (<OverrideInput key={`${indicatorKey}-${plotKey}`} id={`${indicatorKey}-${plotKey}`} label={plotKey.replace('.', ' ')} plotKey={plotKey} value={currentIndiSettings.overrides?.[plotKey] ?? defaultIndiOverrides[plotKey]} onChange={(value) => handleGenericOverrideChange(indicatorKey, plotKey, value)} typeHint={typeof defaultIndiOverrides[plotKey]} />))}
      </div>);
  }; 

   const renderEmaOverridesList = () => {
    if (!indicatorSettings.emas?.enabled) return null;
    return (
      <div className="pl-6 mt-3 space-y-2 border-t border-gray-200 pt-3">
        <h4 className="text-xs font-semibold text-textSecondary mb-1">EMA Visual Overrides:</h4>
        {indicatorSettings.emas.periods.map((_period, index) => (
          <div key={`ema-${index}-overrides-group`} className="pl-4 mt-1 mb-2 space-y-1 border-l border-gray-300 ml-2 py-1">
            <h5 className="text-xs font-medium text-textSecondary">EMA Period {index + 1} (Value: {_period ?? 'N/A'}):</h5>
            {Object.keys(DEFAULT_INDICATOR_SETTINGS.emas.overrides[0] || {}).map(plotKey => (
              <OverrideInput key={`ema-${index}-${plotKey}`} id={`ema-${index}-${plotKey}`} label={plotKey.replace('Plot.', '')} plotKey={plotKey} value={indicatorSettings.emas.overrides[index]?.[plotKey] ?? DEFAULT_INDICATOR_SETTINGS.emas.overrides[0]?.[plotKey]} onChange={(value) => handleEmaSpecificOverrideChange(index, plotKey, value)} typeHint={typeof (DEFAULT_INDICATOR_SETTINGS.emas.overrides[0]?.[plotKey])} />
            ))}
          </div>))}
      </div>);
  }; 
  
  if (isLoading && !showForm) { return <p className="text-textPrimary">Loading configurations...</p>; }
  if (error && !showForm && !isLoading) { return <p className="text-red-500 p-4 bg-red-100 rounded-md">Error: {error}</p>; }

  return (
    <div className="space-y-6">
      {!showForm && (
        <div> 
          <button onClick={handleCreateNew} className="btn btn-primary mb-4">
            Create New Indicator Template
          </button>
          {configs.length === 0 && !isLoading && <p className="text-textSecondary">No indicator templates found.</p>}
          {configs.length > 0 && (
             <div className="overflow-x-auto bg-backgroundSurface shadow-md rounded-lg">
              <table className="min-w-full divide-y divide-borderDefault">
                <thead className="bg-backgroundAlt">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Template Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-textSecondary uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-backgroundSurface divide-y divide-borderDefault">
                  {configs.map(config => {
                    // Ensure type-safe comparison for user IDs
                    const hasCurrentUser = currentUserId !== null && currentUserId !== undefined;
                    const isOwner = hasCurrentUser && config.user !== undefined && String(config.user) === String(currentUserId);
                    
                    // UI Rule: Only owners can modify their templates.
                    // Staff status does not grant edit rights over OTHERS' templates in this UI.
                    // Backend may have broader admin/staff permissions.
                    const canModify = isOwner;

                    return (
                      <tr key={config.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-textPrimary">{config.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-textSecondary">
                          {config.is_global ? 
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Global</span> : 
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Private</span>
                          }
                          {!config.is_global && hasCurrentUser && !isOwner && (
                            <span className="text-xs italic"> (Other user's)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          {canModify ? (
                            <>
                              <button onClick={() => handleEdit(config)} className="text-primary hover:text-primary-dark">Edit</button>
                              <button onClick={() => handleDelete(config.id)} className="text-red-600 hover:text-red-800">Delete</button>
                            </>
                          ) : (
                            hasCurrentUser ? (
                              <span className="text-xs text-textSecondary italic">View only</span>
                            ) : (
                              <span className="text-xs text-textSecondary italic">Checking…</span>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {showForm && (
        <form onSubmit={handleSubmitForm} className="p-6 bg-backgroundSurface shadow-xl rounded-lg space-y-6 max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold text-textPrimary mb-6">{editingConfig ? 'Edit' : 'Create New'} Indicator Template</h2>
          {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">Error: {error}</p>}
          
          <div>
            <label htmlFor="configName" className="block text-sm font-medium text-textPrimary mb-1">Template Name</label>
            <input type="text" id="configName" value={configName} onChange={e => setConfigName(e.target.value)} required className="input-primary w-full" />
          </div>

          <div className="mt-4">
            <label htmlFor="isGlobalConfig" className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" id="isGlobalConfig" checked={isGlobalConfig} onChange={e => setIsGlobalConfig(e.target.checked)} className="form-checkbox h-5 w-5 text-primary rounded"/>
              <span className="text-sm font-medium text-textPrimary">Make this template Global (shareable with others)?</span>
            </label>
          </div>
          
          <fieldset className="border border-borderDefault p-4 rounded-md mt-4">
            <legend className="text-lg font-medium text-textPrimary px-2">Indicator Settings</legend>
            {Object.keys(DEFAULT_INDICATOR_SETTINGS).map(indicatorKey => (
              <div key={indicatorKey} className="p-3 my-2 border-b border-borderDefault last:border-b-0">
                <label className="flex items-center space-x-2 mb-2">
                  <input type="checkbox" checked={indicatorSettings[indicatorKey]?.enabled || false} onChange={() => handleIndicatorToggle(indicatorKey)} className="form-checkbox h-5 w-5 text-primary" />
                  <span className="text-md font-medium text-textPrimary uppercase">{indicatorKey}</span>
                </label>
                {indicatorSettings[indicatorKey]?.enabled && (
                  <div className="pl-6">
                    {indicatorKey === 'emas' && ( <>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Source</label><select value={indicatorSettings.emas.source} onChange={e => handleIndicatorParamChange('emas', 'source', e.target.value)} className="select-primary w-full mb-2">{INDICATOR_SOURCE_CHOICES.map(src => <option key={src} value={src}>{src}</option>)}</select></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Periods:</label>
                          {indicatorSettings.emas.periods.map((period, index) => (<div key={index} className="flex items-center space-x-2 mb-1">
                                  <input type="number" value={period ?? ''} onChange={e => handleEmaPeriodsChange(index, e.target.value)} className="input-primary w-20" placeholder="e.g. 20"/>
                                  <button type="button" onClick={() => removeEmaPeriod(index)} className="text-red-500 hover:text-red-700 text-xs">Remove</button>
                              </div>))}
                          <button type="button" onClick={addEmaPeriod} className="btn btn-secondary btn-sm mt-1 text-xs">Add EMA Period</button>
                        </div>
                        {renderEmaOverridesList()}
                      </>)}
                    {indicatorKey === 'dmi' && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">DI Length</label><input type="number" value={indicatorSettings.dmi.di_length ?? ''} onChange={e => handleIndicatorParamChange('dmi', 'di_length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">ADX Smoothing</label><input type="number" value={indicatorSettings.dmi.adx_smoothing ?? ''} onChange={e => handleIndicatorParamChange('dmi', 'adx_smoothing', e.target.value, true)} className="input-primary w-full" /></div>
                        {renderOverridesSection(indicatorKey)}
                    </div>)}
                    {indicatorKey === 'stoch_rsi' && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">RSI Length</label><input type="number" value={indicatorSettings.stoch_rsi.rsi_length ?? ''} onChange={e => handleIndicatorParamChange('stoch_rsi', 'rsi_length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Stochastic Length</label><input type="number" value={indicatorSettings.stoch_rsi.stoch_length ?? ''} onChange={e => handleIndicatorParamChange('stoch_rsi', 'stoch_length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">K Smooth</label><input type="number" value={indicatorSettings.stoch_rsi.k_smooth ?? ''} onChange={e => handleIndicatorParamChange('stoch_rsi', 'k_smooth', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">D Smooth</label><input type="number" value={indicatorSettings.stoch_rsi.d_smooth ?? ''} onChange={e => handleIndicatorParamChange('stoch_rsi', 'd_smooth', e.target.value, true)} className="input-primary w-full" /></div>
                        {renderOverridesSection(indicatorKey)}
                    </div>)}
                    {indicatorKey === 'rsi' && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Length</label><input type="number" value={indicatorSettings.rsi.length ?? ''} onChange={e => handleIndicatorParamChange('rsi', 'length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Smoothing Line</label><select value={indicatorSettings.rsi.smoothingLine} onChange={e => handleIndicatorParamChange('rsi', 'smoothingLine', e.target.value)} className="select-primary w-full"><option value="SMA">SMA</option><option value="EMA">EMA</option><option value="WMA">WMA</option></select></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Smoothing Length</label><input type="number" value={indicatorSettings.rsi.smoothingLength ?? ''} onChange={e => handleIndicatorParamChange('rsi', 'smoothingLength', e.target.value, true)} className="input-primary w-full" /></div>
                        {renderOverridesSection(indicatorKey)}
                    </div>)}
                    {indicatorKey === 'macd' && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Fast Length</label><input type="number" value={indicatorSettings.macd.fast_length ?? ''} onChange={e => handleIndicatorParamChange('macd', 'fast_length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Slow Length</label><input type="number" value={indicatorSettings.macd.slow_length ?? ''} onChange={e => handleIndicatorParamChange('macd', 'slow_length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Signal Length</label><input type="number" value={indicatorSettings.macd.signal_length ?? ''} onChange={e => handleIndicatorParamChange('macd', 'signal_length', e.target.value, true)} className="input-primary w-full" /></div>
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Source</label><select value={indicatorSettings.macd.source} onChange={e => handleIndicatorParamChange('macd', 'source', e.target.value)} className="select-primary w-full">{INDICATOR_SOURCE_CHOICES.map(src => <option key={src} value={src}>{src}</option>)}</select></div>
                        {renderOverridesSection(indicatorKey)}
                    </div>)}
                    {indicatorKey === 'cmf' && (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><label className="block text-xs font-medium text-textSecondary mb-1">Length</label><input type="number" value={indicatorSettings.cmf.length ?? ''} onChange={e => handleIndicatorParamChange('cmf', 'length', e.target.value, true)} className="input-primary w-full" /></div>
                        {renderOverridesSection(indicatorKey)}
                    </div>)}
                  </div>
                )}
              </div>
            ))}
          </fieldset>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn btn-secondary">Cancel</button>
            <button type="submit" disabled={isLoading} className="btn btn-primary">
              {isLoading ? (editingConfig ? 'Saving...' : 'Creating...') : (editingConfig ? 'Save Changes' : 'Create Template')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ChartSnapshotConfigManager;
